import { Modal } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IRelatedArticles } from "../../../../../../@types/Articles";
import { TextField } from "../../../../../../components/TextField";
import { useHospitalArticlesForPicker } from "../../../../../../hooks/queries/articles";
import {
  articlePickerModalCss,
  ArticleRow,
  CancelButton,
  Container,
  EmptyState,
  Footer,
  ListContainer,
  LoadingState,
  SearchContainer,
} from "./ArticlePickerModal.styles";

type ArticlePickerModalProps = {
  open: boolean;
  patientId?: number;
  selectedArticleId?: string;
  onSelect: (article: IRelatedArticles) => void;
  onClose: () => void;
};

export function ArticlePickerModal({
  open,
  patientId,
  selectedArticleId,
  onSelect,
  onClose,
}: ArticlePickerModalProps) {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");

  const { articles, isFetching, isError } = useHospitalArticlesForPicker(
    open,
    patientId
  );

  const filteredArticles = useMemo(() => {
    if (!articles) return [];
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) return articles;
    return articles.filter((article) =>
      article.articleName.toLowerCase().includes(trimmed)
    );
  }, [articles, search]);

  const handleClose = () => {
    setSearch("");
    onClose();
  };

  const handleSelect = (article: IRelatedArticles) => {
    onSelect(article);
    setSearch("");
  };

  const renderBody = () => {
    if (isFetching) {
      return (
        <LoadingState>
          {t("patientPage_messages_articlePicker_loading")}
        </LoadingState>
      );
    }

    if (isError) {
      return (
        <EmptyState>
          <strong>{t("patientPage_messages_articlePicker_errorTitle")}</strong>
          <span>{t("patientPage_messages_articlePicker_tryAgain")}</span>
        </EmptyState>
      );
    }

    if (!articles || articles.length === 0) {
      return (
        <EmptyState>
          <strong>{t("patientPage_messages_articlePicker_noneTitle")}</strong>
          <span>{t("patientPage_messages_articlePicker_noneBody")}</span>
        </EmptyState>
      );
    }

    if (filteredArticles.length === 0) {
      return (
        <EmptyState>
          <span>
            {t("patientPage_messages_articlePicker_noMatches", { search })}
          </span>
        </EmptyState>
      );
    }

    return filteredArticles?.map((article) => (
      <ArticleRow
        key={article.articleId}
        type="button"
        selected={article.articleId === selectedArticleId}
        onClick={() => handleSelect(article)}
      >
        {article.articleName}
      </ArticleRow>
    ));
  };

  return (
    <Modal
      title={t("patientPage_messages_articlePicker_title")}
      open={open}
      footer={null}
      width={560}
      zIndex={3050}
      styles={{ mask: { pointerEvents: "all" } }}
      onCancel={handleClose}
      className={articlePickerModalCss()}
      destroyOnHidden
      centered
    >
      <Container>
        <SearchContainer>
          <TextField
            label=""
            name="article-picker-search"
            value={search}
            placeholder={t("patientPage_messages_articlePicker_search")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </SearchContainer>
        <ListContainer>{renderBody()}</ListContainer>
        <Footer>
          <CancelButton type="button" onClick={handleClose}>
            {t("common_cancel")}
          </CancelButton>
        </Footer>
      </Container>
    </Modal>
  );
}
